import React from 'react';
import {
  Box, Typography, Stack, FormControl, Select, MenuItem,
  FormControlLabel, Switch,
} from '@mui/material';

const navy = '#1e3a5f';
const selectSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const SwitchRow = ({ label, hint, checked, onChange }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Box>
      <Typography sx={{ fontSize: '0.875rem', color: '#111827', fontWeight: 500 }}>{label}</Typography>
      {hint && <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>{hint}</Typography>}
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: navy },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: navy },
      }}
    />
  </Stack>
);

const SectionBox = ({ title, children }) => (
  <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 2 }}>
    <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem', mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/**
 * InyaneiSettingsStep
 * שלב הגדרות ענייני היום בוויזרד.
 *
 * Props:
 *   formData  { edah, showTachanun, showHallel, showPrayerAdditions, ... }
 *   onChange   (partial) => void
 */
const InyaneiSettingsStep = ({ formData, onChange }) => {
  const edah                    = formData?.edah                    ?? 'ashkenaz';
  const showTachanun            = formData?.showTachanun            ?? true;
  const showHallel              = formData?.showHallel              ?? true;
  const showPrayerAdditions     = formData?.showPrayerAdditions     ?? true;
  const showSeasonalPrayers     = formData?.showSeasonalPrayers     ?? true;
  const showSeasonalTransitions = formData?.showSeasonalTransitions ?? true;
  const showSelichot            = formData?.showSelichot            ?? true;
  const showWeekdayPrayers      = formData?.showWeekdayPrayers      ?? true;
  const showShabbatAdditions    = formData?.showShabbatAdditions    ?? true;
  const showMotzaeiShabbat      = formData?.showMotzaeiShabbat      ?? true;
  const showSeasonalCustoms     = formData?.showSeasonalCustoms     ?? true;
  const showLiturgy             = formData?.showLiturgy             ?? true;
  const showLeyning             = formData?.showLeyning             ?? false;
  const showHaftarah            = formData?.showHaftarah            ?? false;
  const showMourningPeriods     = formData?.showMourningPeriods     ?? true;
  const showLimudim             = formData?.showLimudim             ?? false;
  const lookaheadDays           = formData?.lookaheadDays           ?? 0;

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        הגדרות ענייני היום
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.5 }}>
        בחר אילו ענייני הלכה ותפילה יוצגו ברכיב. כל קטגוריה ניתנת להפעלה/כיבוי בנפרד.
      </Typography>

      {/* ── נוסח/עדה ── */}
      <SectionBox title="נוסח">
        <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mb: 1.5 }}>
          קובע הבדלי הלכה לפי עדה — סליחות, מוריד הטל, ברכת כהנים, קידוש לבנה ועוד.
        </Typography>
        <FormControl fullWidth size="small">
          <Select value={edah} onChange={(e) => onChange({ edah: e.target.value })} sx={selectSx}>
            <MenuItem value="ashkenaz">אשכנז</MenuItem>
            <MenuItem value="sephardic">ספרד</MenuItem>
            <MenuItem value="mizrahi">עדות המזרח</MenuItem>
          </Select>
        </FormControl>
      </SectionBox>

      {/* ── תפילה ── */}
      <SectionBox title="תפילה">
        <Stack spacing={1.5}>
          <SwitchRow
            label="תחנון"
            hint="מוצג רק כשאין תחנון — עם סיבה"
            checked={showTachanun}
            onChange={(v) => onChange({ showTachanun: v })}
          />
          <SwitchRow
            label="הלל"
            hint="הלל שלם או חצי הלל — עם סיבה"
            checked={showHallel}
            onChange={(v) => onChange({ showHallel: v })}
          />
          <SwitchRow
            label="תוספות בתפילה"
            hint="יעלה ויבוא, על הניסים, עננו, נחם"
            checked={showPrayerAdditions}
            onChange={(v) => onChange({ showPrayerAdditions: v })}
          />
          <SwitchRow
            label="גשם וטל"
            hint="משיב הרוח, מוריד הטל, ותן טל ומטר"
            checked={showSeasonalPrayers}
            onChange={(v) => onChange({ showSeasonalPrayers: v })}
          />
          <SwitchRow
            label="ימי מעבר"
            hint="יום התחלת/הפסקת משיב הרוח או טל ומטר — מודגש"
            checked={showSeasonalTransitions}
            onChange={(v) => onChange({ showSeasonalTransitions: v })}
          />
          <SwitchRow
            label="סליחות"
            hint="ימי סליחות — אלול ועשי״ת"
            checked={showSelichot}
            onChange={(v) => onChange({ showSelichot: v })}
          />
          <SwitchRow
            label="תפילות חול"
            hint="אבינו מלכנו, ק-ל ארך אפים"
            checked={showWeekdayPrayers}
            onChange={(v) => onChange({ showWeekdayPrayers: v })}
          />
        </Stack>
      </SectionBox>

      {/* ── שבת ── */}
      <SectionBox title="שבת">
        <Stack spacing={1.5}>
          <SwitchRow
            label="תוספות שבת"
            hint="אב הרחמים, צדקתך, שבת מברכים + מולד"
            checked={showShabbatAdditions}
            onChange={(v) => onChange({ showShabbatAdditions: v })}
          />
          <SwitchRow
            label="מוצאי שבת"
            hint="ויהי נועם, ויתן לך"
            checked={showMotzaeiShabbat}
            onChange={(v) => onChange({ showMotzaeiShabbat: v })}
          />
        </Stack>
      </SectionBox>

      {/* ── מנהגים וליטורגיה ── */}
      <SectionBox title="מנהגים וליטורגיה">
        <Stack spacing={1.5}>
          <SwitchRow
            label="מנהגים עונתיים"
            hint="לדוד ה' אורי, פרקי אבות, ברכי נפשי, ספירת העומר, עירוב תבשילין"
            checked={showSeasonalCustoms}
            onChange={(v) => onChange({ showSeasonalCustoms: v })}
          />
          <SwitchRow
            label="ליטורגיה"
            hint="מוסף, יזכור, ספרי תורה, קידוש לבנה"
            checked={showLiturgy}
            onChange={(v) => onChange({ showLiturgy: v })}
          />
          <SwitchRow
            label="תקופות אבלות"
            hint="בין המצרים, תשעת הימים, שבוע שחל בו"
            checked={showMourningPeriods}
            onChange={(v) => onChange({ showMourningPeriods: v })}
          />
          <SwitchRow
            label="לימודים יומיים"
            hint="דף יומי, משנה יומית, רמב״ם ועוד"
            checked={showLimudim}
            onChange={(v) => onChange({ showLimudim: v })}
          />
        </Stack>
      </SectionBox>

      {/* ── קריאה ── */}
      <SectionBox title="קריאת תורה">
        <Stack spacing={1.5}>
          <SwitchRow
            label="קריאת התורה"
            hint="ספר, פרק ופסוקים"
            checked={showLeyning}
            onChange={(v) => onChange({ showLeyning: v })}
          />
          <SwitchRow
            label="הפטרה"
            hint="מראה מקום ההפטרה"
            checked={showHaftarah}
            onChange={(v) => onChange({ showHaftarah: v })}
          />
        </Stack>
      </SectionBox>

      {/* ── הצצה קדימה ── */}
      <SectionBox title="הצצה קדימה">
        <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mb: 1.5 }}>
          הצגת שינויים צפויים בימים הקרובים — רק דברים שונים מהיום (ימי מעבר, חגים, יזכור ועוד).
        </Typography>
        <FormControl fullWidth size="small">
          <Select value={lookaheadDays} onChange={(e) => onChange({ lookaheadDays: e.target.value })} sx={selectSx}>
            <MenuItem value={0}>היום בלבד</MenuItem>
            <MenuItem value={3}>3 ימים קדימה</MenuItem>
            <MenuItem value={7}>שבוע קדימה</MenuItem>
          </Select>
        </FormControl>
      </SectionBox>
    </Box>
  );
};

export default InyaneiSettingsStep;
